import { tv, type VariantProps } from 'tailwind-variants'

/**
 * Maps the design-system type scale (declared in index.css @theme) to props.
 * `variant` controls visual size + paired line-height; `as` controls the
 * rendered HTML tag — they are intentionally independent so you can render,
 * e.g., heading-sized text on a <span> without breaking the document outline.
 */
const text = tv({
  variants: {
    variant: {
      h1: 'text-h1',
      h2: 'text-h2',
      h3: 'text-h3',
      lg: 'text-lg',
      base: 'text-base',
      sm: 'text-sm',
      caption: 'text-caption',
      xs: 'text-xs',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    // Boolean variant: pass `mono` to switch to the Menlo mono stack.
    mono: { true: 'font-mono' },
  },
  defaultVariants: {
    variant: 'base',
    weight: 'normal',
  },
}, {
  // tailwind-merge only knows the default font-size names (text-xs/sm/base/lg…), so it
  // treats our custom-named sizes (text-h1/h2/h3/caption) as unknown `text-*` and lumps
  // them with the text-color group — dropping the size whenever a `text-{color}` is also
  // passed via className. Register them as font-sizes so size + color coexist (and sizes
  // still override each other correctly).
  twMergeConfig: {
    extend: {
      classGroups: {
        'font-size': [{ text: ['h1', 'h2', 'h3', 'caption'] }],
      },
    },
  },
})

type TextVariants = VariantProps<typeof text>

export type TextProps<T extends React.ElementType = 'span'> = TextVariants & {
  /** The HTML element / component to render. Defaults to <span>. */
  as?: T
  className?: string
  children: React.ReactNode
} & Omit<React.ComponentPropsWithoutRef<T>, 'as' | 'className' | 'children'>

export function Text<T extends React.ElementType = 'span'>({
  as,
  variant,
  weight,
  mono,
  className,
  children,
  ...rest
}: TextProps<T>) {
  const Tag = (as ?? 'span') as React.ElementType
  return (
    <Tag className={text({ variant, weight, mono, className })} {...rest}>
      {children}
    </Tag>
  )
}